'use client';

import { type ComponentType, useEffect, useState } from 'react';
import { cn } from '../cn';
import { Button } from '../ui/button';
import type { MobileNavProps } from './mobile-nav';
import MenuIcon from '../icons/menu-icon';

type LazyMobileNavProps = Omit<MobileNavProps, 'defaultOpen'>;

let mobileNavPromise: Promise<ComponentType<MobileNavProps>> | null = null;

function loadMobileNav() {
  if (!mobileNavPromise) {
    const request = import('./mobile-nav').then(module => module.default);

    mobileNavPromise = request;
    void request.catch(() => {
      if (mobileNavPromise === request) mobileNavPromise = null;
    });
  }

  return mobileNavPromise;
}

export function LazyMobileNav(props: LazyMobileNavProps) {
  const [MobileNav, setMobileNav] =
    useState<ComponentType<MobileNavProps> | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (!shouldLoad || MobileNav) return;

    let isCancelled = false;

    void loadMobileNav()
      .then(LoadedMobileNav => {
        if (isCancelled) return;

        setMobileNav(() => LoadedMobileNav);
        setShouldLoad(false);
      })
      .catch(error => {
        console.error('[MobileNav] Không thể tải menu mobile:', error);
        if (!isCancelled) setShouldLoad(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [MobileNav, shouldLoad]);

  const preloadMobileNav = () => {
    void loadMobileNav();
  };

  if (MobileNav) {
    return <MobileNav {...props} defaultOpen />;
  }

  return (
    <div className="flex items-center justify-between gap-3 transition-all duration-300">
      <Button
        type="button"
        aria-label="Mở menu"
        aria-expanded={false}
        aria-haspopup="dialog"
        variant="ghost"
        size="icon"
        disabled={shouldLoad}
        onClick={() => setShouldLoad(true)}
        onFocus={preloadMobileNav}
        onPointerDown={preloadMobileNav}
        onPointerEnter={preloadMobileNav}
        className={cn(
          'border-base-border bg-surface-pure text-on-surface hover:bg-surface-variant hover:text-on-surface flex size-10 items-center justify-center rounded-[14px] border p-0 ring-0 transition-all duration-300 focus:ring-0 focus-visible:ring-0 xl:hidden [&_svg:not([class*="size-"])]:size-6',
          shouldLoad && 'opacity-70'
        )}
      >
        <MenuIcon size={24} aria-hidden />
      </Button>
    </div>
  );
}
